import "./style.css";
import "./style.css";
import React, { useState, useEffect } from "react";
import * as yup from "yup";
import { useFormik } from "formik";
import { useNavigate, useLocation } from "react-router-dom";
import { failed, success } from "@components/Common/Toastify";
import { resetPassword } from "../../API/Auth";
import logo from "../../Assets/Images/Lendriva_logo.jpg";
import hide from "../../Assets/Images/Hide.png";

const ResetPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const roleType = location.state?.roleType;
  const email = location?.state?.email || "";
  const [btnLoading, setBtnLoading] = useState(false);
  const [selectedUserType, setSelectedUserType] = useState<
    "borrower" | "lender"
  >("borrower");
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);

  useEffect(() => {
    const loginPage = document.querySelector(".onboarding-page");

    if (loginPage) {
      loginPage.classList.remove("theme-borrower", "theme-lender");
      loginPage.classList.add(`theme-${roleType}`);
    }
  }, [roleType]);

  useEffect(() => {
    if (roleType === "borrower" || roleType === "lender") {
      setSelectedUserType(roleType);
    }
  }, [roleType]);

  useEffect(() => {
    if (!email) {
      failed("Email is missing. Please go back and request a reset again.");
      navigate("/auth/forgot-password");
    }
  }, [email, navigate]);

  const formik = useFormik({
    initialValues: {
      otp: "",
      password: "",
      confirmPassword: "",
    },
    validationSchema: yup.object({
      otp: yup
        .string()
        .required("Otp is required")
        .matches(/^$|^\S+.*/, "Only blankspaces is not valid."),
      password: yup
        .string()
        .required("Password is required")
        .matches(
          /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#\\$%\\^&\\*])(?=.{8,})/,
          "Must contain 8 characters, one uppercase, one lowercase, one number and one special case character"
        ),
      confirmPassword: yup
        .string()
        .required("Confirm Password is required")
        .oneOf([yup.ref("password"), null], "Passwords must match"),
    }),
    onSubmit: (values) => {
      setBtnLoading(true);
      resetPassword({
        email: email?.toLowerCase(),
        userType: roleType,
        otp: values.otp,
        password: values.password,
        confirmPassword: values.confirmPassword,
      })
        .then((res: any) => {
          navigate("/auth/successfully-reset", {
            state: {
              roleType: roleType,
            },
          });
          success(res.data?.message);
        })
        .catch((err) => {
          failed(err.response?.data?.message || "Reset failed.");
        })
        .finally(() => setBtnLoading(false));
    },
  });

  const otpRefs = React.useRef<Array<HTMLInputElement | null>>([]);

  const handleOtpChange = (e: React.ChangeEvent<HTMLInputElement>, idx: number) => {
    const value = e.target.value;
    if (!/^\d?$/.test(value)) return;

    const otpArray = formik.values.otp.split("").concat(Array(6).fill("")).slice(0, 6);
    otpArray[idx] = value;
    const newOtp = otpArray.join("");
    formik.setFieldValue("otp", newOtp);
    if (value && idx < 5) {
      otpRefs.current[idx + 1]?.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, idx: number) => {
    if (e.key === "Backspace" && !e.currentTarget.value && idx > 0) {
      otpRefs.current[idx - 1]?.focus();
    }
  };

  // Toggle visibility for password field
  const togglePasswordVisibility = () => {
    setPasswordVisible(!passwordVisible);
  };

  // Toggle visibility for confirm password field
  const toggleConfirmPasswordVisibility = () => {
    setConfirmPasswordVisible(!confirmPasswordVisible);
  };



  return (
    <>
      <div className="row onboarding-page m-0 p-0">
        <div className="container-fluid reset-pw">
         

          <div className="row lower-div mx-auto">

             <div className="close-icon cursor-pointer text-end mb-0">
            <svg
              width="24"
              height="25"
              viewBox="0 0 24 25"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
            </svg>
         
            <div className="col text-center logo-fit">
              <img src={logo} alt="Logo" className="img-fluid" />
            </div>
          </div>

          <div className="row">
            <div className="col">
              <div className="credentials-box">
                <h2 className="text-center set-new">Set New Password</h2>
                <p className="text-center">
                  Your new password must be different from previously used ones.
                </p>

                <p className="text-center mb-4">
                  Enter the verification code sent to your email
                </p>

                <div className="d-flex justify-content-center mb-4 otp-block">
                  {[...Array(6)].map((_, idx) => (
                    <input
                      key={idx}
                      type="text"
                      maxLength={1}
                      className="form-control text-center otp-box"
                      onChange={(e) => handleOtpChange(e, idx)}
                      onKeyDown={(e) => handleKeyDown(e, idx)}
                      ref={(el) => (otpRefs.current[idx] = el)}
                    />
                  ))}
                </div>
                {formik.touched.otp && formik.errors.otp && (
                  <div className="text-danger text-center small mb-3">
                    {formik.errors.otp}
                  </div>
                )}

                <form onSubmit={formik.handleSubmit} className="onboarding-form p-0">
                  <div className="mb-4">
                    <label htmlFor="password" className="form-label">
                      New Password <span className="text-orange">*</span>
                    </label>
                    <div className="pw-input-block position-relative">
                      <input
                        type={passwordVisible ? "text" : "password"}
                        id="password"
                        className="form-control"
                        placeholder="Password"
                        {...formik.getFieldProps("password")}

                      />
                      <div
                        className="eye-icon-auth"
                        onClick={togglePasswordVisibility}
                      >
                        <img src={hide} alt="eye icon" className="img-fluid" />
                      </div>
                    </div>

                    {formik.touched.password && formik.errors.password && (
                      <div className="text-danger small">
                        {formik.errors.password}
                      </div>
                    )}
                    <p className="m-0 pw-text">
                      Should contain <strong>uppercase</strong>, <strong>lowercase</strong>, <strong>number</strong> & <strong>symbol</strong>.
                    </p>
                  </div>

                  <div className="mb-4">
                    <label htmlFor="confirmPassword" className="form-label">
                      Confirm Password <span className="text-orange">*</span>
                    </label>
                    <div className="pw-input-block position-relative">
                      <input
                        type={confirmPasswordVisible ? "text" : "password"}
                        id="confirmPassword"
                        className="form-control"
                        placeholder="Confirm Password"
                        {...formik.getFieldProps("confirmPassword")}
                      />
                      <div
                        className="eye-icon-auth"
                        onClick={togglePasswordVisibility} // Toggle password visibility on click
                      >
                        <img src={hide} alt="eye icon" className="img-fluid" />
                      </div>
                    </div>
                    {formik.touched.confirmPassword && formik.errors.confirmPassword && (
                      <div className="text-danger small">
                        {formik.errors.confirmPassword}
                      </div>
                    )}
                  </div>

                  <button
                    type="submit"
                    className="btn btn-borrower signin w-100"
                    disabled={btnLoading}
                  >
                    {btnLoading ? "Processing..." : "Change Password"}
                  </button>
                </form>


              </div>
            </div>
          </div>
        </div>
         </div>
      </div>
    </>
  );
};

export default ResetPassword;
